// src/services/profileService.js
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import { auth, db } from "../firebase";
import { getDeviceDetails } from "../utils/commonFunctions";
import { mapToFriendlyNetworkError } from "../utils/networkError";

export const ACCOUNT_TYPES = {
  INDIVIDUAL: "individual",
  BUSINESS: "business",
};

export const ONBOARDING_STATUS = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  SKIPPED: "skipped",
  COMPLETED: "completed",
};

// Helper function to get user profile from localStorage
export const getUserProfileFromLocalStorage = () => {
  const userProfile = localStorage.getItem("userProfile");
  return userProfile ? JSON.parse(userProfile) : null;
};

// Firestore profile functions
export const updateProfileInFirestore = async (uid, updatedProfile) => {
  if (!uid) {
    throw new Error("UID is undefined.");
  }

  const userRef = doc(db, "users", uid);

  // Destructure out `createdAt` (if it exists); everything else goes into `profileData`
  const { createdAt, ...profileData } = updatedProfile;

  // Now only update with fields in `profileData`, so `createdAt` is never written
  await updateDoc(userRef, profileData);
};

// Update the user's display name in the default users profile and the
// Firebase Auth account so it stays consistent everywhere it's read.
export const updateUserDisplayName = async (uid, displayName) => {
  if (!uid) {
    throw new Error("UID is undefined.");
  }
  const name = String(displayName || "").trim();
  if (!name) {
    throw new Error("Please enter a name.");
  }

  // Default users profile.
  await updateProfileInFirestore(uid, {
    displayName: name,
    updatedAt: serverTimestamp(),
  });

  // Firebase Auth account (only for the currently signed-in user).
  if (auth?.currentUser && auth.currentUser.uid === uid) {
    await updateProfile(auth.currentUser, { displayName: name });
  }

  return name;
};

//Save FCM Token to Firestore
export const addFcmTokenToFirestore = async (uid, token) => {
  if (!uid || !token) return;

  const deviceInfo = getDeviceDetails();
  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);
  const userData = userSnap.exists() ? userSnap.data() : {};

  const existingTokens = userData.fcmTokens || [];

  // Remove any existing entry for the current device
  const filteredTokens = existingTokens.filter(
    (entry) => entry.device !== deviceInfo.device
  );

  // Add updated token for the current device
  filteredTokens.push({
    token,
    device: deviceInfo.device,
    lastUpdated: new Date(),
  });

  await updateDoc(userRef, {
    fcmTokens: filteredTokens,
  });
};
//Remove FCM Token from Firestore
export const removeFcmTokenFromFirestore = async (uid) => {
  if (!uid) return;

  const deviceInfo = getDeviceDetails(); // e.g., { device: "SM-G955U", ... }
  if (!deviceInfo?.device) return;

  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) return;

  const userData = userSnap.data();
  const existingTokens = userData.fcmTokens || [];

  // Filter out the token for the current device
  const updatedTokens = existingTokens.filter(
    (entry) => entry.device !== deviceInfo.device
  );

  await updateDoc(userRef, {
    fcmTokens: updatedTokens,
  });

  localStorage.removeItem("fcmToken");
  localStorage.removeItem("fcmEmailKey");
};

// Fetch Firestore profile
export const fetchUserProfileFromFirestore = async (uid) => {
  if (!uid) throw new Error("User ID is required to fetch profile");

  // console.log("Fetching Firestore profile for UID:", uid); // Debugging Log

  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    // console.log("Firestore User Profile Data:", userSnap.data()); // Debugging Log
    // The document id IS the uid, but the `uid` FIELD is only written by
    // buildUserProfilePayload — a document created before it, or by a backfill,
    // has none. Callers then read `userProfile.uid` as undefined, which is how
    // an owner check comparing two empty strings started passing for everyone.
    // Stamping it here fixes every consumer at once, and the id is the
    // authority when both are present.
    return {...userSnap.data(), uid: userSnap.id};
  } else {
    console.log("No such user profile in Firestore, returning null.");
    return null; // Return null instead of throwing an error
  }
};

export const waitForUserProfileFromFirestore = async (
  uid,
  { maxAttempts = 20, delayMs = 250 } = {}
) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const profile = await fetchUserProfileFromFirestore(uid);
    if (profile) {
      return profile;
    }

    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => {
        setTimeout(resolve, delayMs);
      });
    }
  }

  return null;
};
// Fetch all users basic info from firestore
export const fetchAllUsersBasicInfoFromFirestore = async () => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);

    const users = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        uid: doc.id,
        displayName: data.displayName || null,
        email: data.email || null,
        phoneNumber: data.phoneNumber || null,
        profileImage: data.profileImage || null
      };
    });

    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
};
// Delete Firestore profile
export const deleteUserProfileFromFirestore = async (uid) => {
  if (!uid) {
    throw new Error("UID is undefined.");
  }

  const userRef = doc(db, "users", uid);
  await deleteDoc(userRef);
};

export const createUserProfileInFirestore = async (uid, profileData) => {
  if (!uid) {
    throw new Error("UID is undefined.");
  }

  // ensure the 'uid' field is part of the profileData
  if (!profileData.uid) {
    profileData.uid = uid;
  }

  const userRef = doc(db, "users", uid);

  // merge in a server-side timestamp for createdAt
  await setDoc(userRef, {
    ...profileData,
    updatedAt: serverTimestamp(),
    lastActive: serverTimestamp(),
    createdAt: serverTimestamp(),
  }, { merge: true });
};

// Azure profile fetching function
export const fetchAzureUserProfile = async (email) => {
  const getUserProfileUrl =
    process.env.REACT_APP_GET_KBEAN_USER_PROFILE_BY_EMAIL_URL;
  const apiKey = process.env.REACT_APP_SEARCH_API_KEY;

  console.log("Fetching Azure user profile for email:", email); // Log email

  try {
    const response = await fetch(getUserProfileUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ApiKey: apiKey,
      },
      body: JSON.stringify({ EmailAddress: email }),
    });

    console.log("Azure API response status:", response.status); // Log status code

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();

    console.log("Azure profile response data:", data); // Log the entire response

    return data;
  } catch (error) {
    console.error("Error fetching Azure profile:", error); // Log any errors
    throw mapToFriendlyNetworkError(
      error,
      "Profile details will load once your connection is back."
    );
  }
};
//Get Azure profile By Token
export const fetchAzureUserProfileByToken = async () => {
  const getUserProfileUrl = process.env.REACT_APP_GET_KBEAN_USER_PROFILE_BY_TOKEN_URL;
  const authResponseData = JSON.parse(localStorage.getItem("authResponseData"));
  const token = authResponseData?.token;

  try {
    const response = await fetch(`${getUserProfileUrl}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching Azure profile:", error);
    throw mapToFriendlyNetworkError(
      error,
      "Profile details will load once your connection is back."
    );
  }
};
export const updateAzureUserProfile = async (formData) => {
  const updateUserProfileUrl = process.env.REACT_APP_UPDATE_KBEAN_USER_PROFILE_URL;
  const authResponseData = JSON.parse(localStorage.getItem("authResponseData"));
  const token = authResponseData?.token;

  try {
    const response = await fetch(updateUserProfileUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to update profile. Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating Azure profile:", error);
    throw mapToFriendlyNetworkError(
      error,
      "We couldn't save your profile because your connection dropped."
    );
  }
};

// Check username availability via 3rd party API
export const checkUserNameAvailability = async (userName) => {
  const checkUserNameAvailabilityURL =
    process.env.REACT_APP_CHECK_USERNAME_AVAILABILITY_URL;
  try {
    const response = await fetch(`${checkUserNameAvailabilityURL}${userName}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    return data.response.message === "UserName available";
  } catch (error) {
    throw mapToFriendlyNetworkError(
      error,
      "Username availability check needs a stable network connection."
    );
  }
};

// Update Azure user profile with new username
export const updateAzureUserProfileUsername = async (userName, email) => {
  const updateUserNameUrl = process.env.REACT_APP_UPDATE_KBEAN_USERNAME;
  const apiKey = process.env.REACT_APP_SEARCH_API_KEY;

  try {
    const response = await fetch(updateUserNameUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ApiKey: apiKey,
      },
      body: JSON.stringify({ UserName: userName, EmailAddress: email }),
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    throw mapToFriendlyNetworkError(
      error,
      "We couldn't update your username because your connection is unavailable."
    );
  }
};

// Fetch challenges created by a user
export const fetchUserChallenges = async (uid, currentUser) => {
  if (!uid) throw new Error("User ID is required to fetch challenges");

  const challengesRef = collection(db, "challenges");

  // If fetching challenges for the current user, include drafts
  let q;
  if (uid === currentUser) {
    // Fetch both published and unpublished challenges for the current user
    q = query(challengesRef, where("creatorInfo.creatorUid", "==", uid));
  } else {
    // Fetch only published challenges for other users
    q = query(
      challengesRef,
      where("creatorInfo.creatorUid", "==", uid),
      where("published", "==", true)
    );
  }

  try {
    const querySnapshot = await getDocs(q);
    const challenges = [];
    querySnapshot.forEach((doc) => {
      challenges.push({ id: doc.id, ...doc.data() });
    });

    return challenges; // Return the fetched challenges
  } catch (error) {
    console.error("Error fetching challenges:", error);
    throw error;
  }
};

// Fetch submissions created by a user
export const fetchUserSubmissions = async (uid, currentUserUid) => {
  if (!uid) throw new Error("User ID is required to fetch submissions");

  const submissionsRef = collection(db, "submissions");
  const q = query(submissionsRef, where("profileData.uid", "==", uid));

  try {
    const querySnapshot = await getDocs(q);
    const submissions = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Omit submissions with status "draft" if currentUserUid is not equal to uid
      if (data.status === "draft" && currentUserUid !== uid) {
        return; // Skip this submission
      }
      submissions.push({ id: doc.id, ...data });
    });

    return submissions;
  } catch (error) {
    console.error("Error fetching submissions:", error);
    throw error;
  }
};

// Function to update profile image in the relevant collections
export const updateProfileImageInAllCollections = async (
  userId,
  newProfileImageUrl
) => {
  try {
    // Step 1: Update profile image in "challenges" collection
    const challengesRef = collection(db, "challenges");
    const challengesQuery = query(
      challengesRef,
      where("creatorInfo.creatorUid", "==", userId)
    );
    const challengesSnapshot = await getDocs(challengesQuery);

    challengesSnapshot.forEach(async (challengeDoc) => {
      const challengeRef = doc(db, "challenges", challengeDoc.id);
      await updateDoc(challengeRef, {
        "creatorInfo.profileImage": newProfileImageUrl,
      });
    });

    console.log("Updated profile image in challenges collection.");

    // Step 2: Update profile image in "submissions" collection
    const submissionsRef = collection(db, "submissions");
    const submissionsQuery = query(
      submissionsRef,
      where("user.uid", "==", userId)
    );
    const submissionsSnapshot = await getDocs(submissionsQuery);

    submissionsSnapshot.forEach(async (submissionDoc) => {
      const submissionRef = doc(db, "submissions", submissionDoc.id);
      await updateDoc(submissionRef, {
        "user.profileImage": newProfileImageUrl,
      });
    });

    console.log("Updated profile image in submissions collection.");
  } catch (error) {
    console.error("Error updating profile image in all collections:", error);
  }
};

// Function to follow a user
export const followUser = async (currentUser, targetUser) => {
  const followerUserProfile = getUserProfileFromLocalStorage();

  console.log(followerUserProfile.userName);

  try {
    // Construct unique IDs for the followers and following documents
    const followerDocId = `${targetUser.uid}_${currentUser.uid}`; // For followers collection
    const followingDocId = `${currentUser.uid}_${targetUser.uid}`; // For following collection

    // References to follower and following collections
    const followerRef = doc(db, "followers", followerDocId);
    const followingRef = doc(db, "following", followingDocId);

    // Add the follower to the target user's followers collection
    await setDoc(followerRef, {
      uid: targetUser.uid,
      followerUid: followerUserProfile.uid,
      followerDisplayName: followerUserProfile.displayName,
      followerUsername: followerUserProfile.userName,
      followerProfileImage: followerUserProfile.profileImage,
      timestamp: new Date(),
    });

    // Add the current user to the target user's following collection
    await setDoc(followingRef, {
      uid: currentUser.uid,
      followingUid: targetUser.uid,
      followingDisplayName: targetUser.displayName,
      followingUsername: targetUser.userName,
      followingProfileImage: targetUser.profileImage,
      timestamp: new Date(),
    });

    console.log("Successfully followed the user");
  } catch (error) {
    console.error("Error following user:", error);
    throw error;
  }
};

// Function to unfollow a user
export const unfollowUser = async (currentUser, targetUser) => {
  try {
    // Construct unique IDs for the followers and following documents
    const followerDocId = `${targetUser.uid}_${currentUser.uid}`; // For followers collection
    const followingDocId = `${currentUser.uid}_${targetUser.uid}`; // For following collection

    // References to follower and following collections
    const followerRef = doc(db, "followers", followerDocId);
    const followingRef = doc(db, "following", followingDocId);

    // Delete follower and following documents
    await deleteDoc(followerRef);
    await deleteDoc(followingRef);

    console.log("Successfully unfollowed the user");
  } catch (error) {
    console.error("Error unfollowing user:", error);
    throw error;
  }
};

// Function to check if the current user is following the target user
export const isFollowingUser = async (currentUser, targetUserId) => {
  try {
    const followingDocId = `${currentUser.uid}_${targetUserId}`;
    const followingRef = doc(db, "following", followingDocId);
    const followingDoc = await getDoc(followingRef);

    return followingDoc.exists();
  } catch (error) {
    console.error("Error checking following status:", error);
    return false;
  }
};

// Function to fetch followers count for a specific user
export const fetchFollowersCount = async (userId) => {
  try {
    const followersRef = collection(db, "followers");
    const followersQuery = query(followersRef, where("uid", "==", userId));
    const followersSnapshot = await getDocs(followersQuery);
    return followersSnapshot.size;
  } catch (error) {
    console.error("Error fetching followers count:", error);
    throw error;
  }
};

// Function to fetch following count for a specific user
export const fetchFollowingCount = async (userId) => {
  try {
    const followingRef = collection(db, "following");
    const followingQuery = query(followingRef, where("uid", "==", userId));
    const followingSnapshot = await getDocs(followingQuery);
    return followingSnapshot.size;
  } catch (error) {
    console.error("Error fetching following count:", error);
    throw error;
  }
};

// Function to fetch artist profile with artworks from Kbean Azure backend for social video interactions
export const getArtistProfile = async (username) => {
  const apiKey = process.env.REACT_APP_SEARCH_API_KEY;
  const url = process.env.REACT_APP_GET_KBEAN_ARTIST_PROFILE_BY_USERNAME;

  const body = JSON.stringify({
    Sort: '{"Sale.SaleStart":-1}',
    Page: 0,
    Size: 2000,
    GroupBy: "",
    FilterMatureArtworks: false,
    FilterKbeanSelectOnly: false,
    Criteria: [
      {
        type: "username",
        value: username,
      },
    ],
  });

  const headers = {
    "Content-Type": "application/json",
    ApiKey: apiKey,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: body,
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch artist profile:", error);
    throw mapToFriendlyNetworkError(
      error,
      "Artist profile data will load once your connection is back."
    );
  }
};

// Helper function to fetch a user profile from Firestore by UID
export const fetchUserProfile = async (uid) => {
  if (!uid) throw new Error("User ID is required to fetch profile");

  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return { id: userSnap.id, ...userSnap.data() };
  } else {
    console.log("No such user profile in Firestore, returning null.");
    return null;
  }
};

// Fetch the current logged-in user's profile from Firestore
export const fetchCurrentUserProfile = async (currentUser) => {
  if (currentUser) {
    try {
      const userProfile = await fetchUserProfile(currentUser.uid);
      return userProfile || currentUser; // Return Firestore profile or auth profile
    } catch (error) {
      console.error("Error fetching Firestore profile:", error);
      throw error;
    }
  } else {
    console.log("No user is signed in.");
    return null; // Return null if no user is logged in
  }
};

// Function to fetch memberships for a user from Firestore
export const fetchUserMembershipsFromFirestore = async (uid) => {
  console.log(`Fetching memberships for UID: ${uid}`); // Debugging Log
  const membershipsRef = collection(db, "memberships");
  const q = query(membershipsRef, where("userId", "==", uid));

  const querySnapshot = await getDocs(q);
  const memberships = [];

  querySnapshot.forEach((doc) => {
    console.log(`Found membership: ${doc.id}`); // Debugging Log
    memberships.push({ id: doc.id, ...doc.data() });
  });

  return memberships;
};

// Fetch the current logged-in user's memberships from Firestore
export const fetchCurrentUserMemberships = async (currentUser) => {
  if (currentUser) {
    try {
      const userMemberships = await fetchUserMembershipsFromFirestore(
        currentUser.uid
      );
      return userMemberships;
    } catch (error) {
      console.error("Error fetching Firestore memberships:", error);
      throw error;
    }
  } else {
    console.log("No user is signed in.");
    return []; // Return an empty array if no user is logged in
  }
};

const toSafeString = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  return String(value);
};

const isNonEmptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

const pickFirstNonEmpty = (...values) => {
  for (const value of values) {
    if (isNonEmptyString(value)) return value;
  }
  return "";
};

const buildUserProfilePayload = ({
  user,
  existingProfile = null,
  azureProfile = null,
}) => {
  const fullNameFromParts = [azureProfile?.firstName, azureProfile?.lastName]
    .map((part) => toSafeString(part).trim())
    .filter(Boolean)
    .join(" ");

  const email = pickFirstNonEmpty(
    azureProfile?.emailAddress,
    existingProfile?.email,
    user?.email
  );

  const displayName = pickFirstNonEmpty(
    azureProfile?.fullName,
    fullNameFromParts,
    existingProfile?.displayName,
    user?.displayName
  ) || " ";

  const userName = pickFirstNonEmpty(
    azureProfile?.userName,
    existingProfile?.userName,
    email
  );

  const phoneNumber = pickFirstNonEmpty(
    azureProfile?.phoneNumber,
    existingProfile?.phoneNumber,
    user?.phoneNumber
  );

  const payload = {
    uid: user.uid,
    bio: pickFirstNonEmpty(azureProfile?.biography, existingProfile?.bio),
    displayName,
    email,
    facebookUrl: pickFirstNonEmpty(
      azureProfile?.facebookProfile,
      existingProfile?.facebookUrl
    ),
    facilitatorAccessToken: toSafeString(
      existingProfile?.facilitatorAccessToken,
      ""
    ),
    fcmTokens: Array.isArray(existingProfile?.fcmTokens)
      ? existingProfile.fcmTokens
      : [],
    hasSignedUpWithINSSA: true,
    heroImage: toSafeString(existingProfile?.heroImage, ""),
    instagramUrl: pickFirstNonEmpty(
      azureProfile?.instagramProfile,
      existingProfile?.instagramUrl
    ),
    orderID: toSafeString(existingProfile?.orderID, ""),
    paymentSource: toSafeString(existingProfile?.paymentSource, ""),
    planId: toSafeString(existingProfile?.planId, ""),
    // Firestore is the source of truth for the profile image: a user's
    // uploaded image (e.g. from inssa-discover) must win over the Azure/Kbean
    // value, which can lag or be stale. Only fall back to Azure/photoURL when
    // Firestore has no image yet. See inssa-discover profileService for the
    // matching guard.
    profileImage: pickFirstNonEmpty(
      existingProfile?.profileImage,
      azureProfile?.profilePicture,
      user?.photoURL
    ),
    subscriptionData:
      existingProfile?.subscriptionData &&
      typeof existingProfile.subscriptionData === "object"
        ? existingProfile.subscriptionData
        : {},
    subscriptionDate:
      existingProfile?.subscriptionDate !== undefined
        ? existingProfile.subscriptionDate
        : null,
    subscriptionID: toSafeString(existingProfile?.subscriptionID, ""),
    subscriptionStatus: toSafeString(existingProfile?.subscriptionStatus, ""),
    twitterUrl: pickFirstNonEmpty(
      azureProfile?.twitterHandle,
      existingProfile?.twitterUrl
    ),
    userName,
    updatedAt: serverTimestamp(),
    lastActive: serverTimestamp(),
  };

  // Do not write business linkage fields on first sign-in.
  // Preserve existing business linkage only for profiles that already have it.
  if (existingProfile && existingProfile.primaryBusinessId !== undefined) {
    payload.primaryBusinessId = existingProfile.primaryBusinessId;
  }

  if (phoneNumber) {
    payload.phoneNumber = phoneNumber;
    payload.phoneVerified = existingProfile?.phoneVerified === true || !!user?.phoneNumber;
  } else if (existingProfile?.phoneVerified === true) {
    payload.phoneVerified = true;
  }

  return payload;
};

export const getOrCreateUserProfile = async (user) => {
  const activeUser = user || auth.currentUser;
  if (!activeUser?.uid) {
    throw new Error("User is required to sync profile.");
  }

  // 1) try existing Firestore profile
  let profile = await fetchUserProfileFromFirestore(activeUser.uid);

  // 2) ensure a Firestore profile exists immediately so auth flows can proceed
  // without waiting on Azure profile APIs.
  if (!profile) {
    const baseData = buildUserProfilePayload({
      user: activeUser,
      existingProfile: null,
      azureProfile: null,
    });
    await createUserProfileInFirestore(activeUser.uid, baseData);
    profile = await fetchUserProfileFromFirestore(activeUser.uid);
  }

  // 3) Continue profile enrichment in the background so auth flows do not stall.
  Promise.resolve()
    .then(async () => {
      let azureProfile = null;
      if (activeUser?.email) {
        try {
          azureProfile = await fetchAzureUserProfile(activeUser.email);
        } catch (error) {
          console.warn("Azure profile fetch failed, continuing with Firebase data:", error);
        }
      }

      const latestProfile = (await fetchUserProfileFromFirestore(activeUser.uid)) || profile || null;
      const data = buildUserProfilePayload({
        user: activeUser,
        existingProfile: latestProfile,
        azureProfile,
      });
      await setDoc(doc(db, "users", activeUser.uid), data, { merge: true });
    })
    .catch((error) => {
      console.warn("Deferred profile enrichment failed:", error);
    });

  return profile;
};

export const setUserAccountType = async (uid, accountType) => {
  if (!uid) throw new Error("UID is undefined.");
  if (!Object.values(ACCOUNT_TYPES).includes(accountType)) {
    throw new Error("Invalid accountType value.");
  }

  const userRef = doc(db, "users", uid);
  const patch = {
    uid,
    accountType,
    onboardingStatus:
      accountType === ACCOUNT_TYPES.BUSINESS
        ? ONBOARDING_STATUS.NOT_STARTED
        : ONBOARDING_STATUS.COMPLETED,
    updatedAt: serverTimestamp(),
  };

  if (accountType === ACCOUNT_TYPES.BUSINESS) {
    patch.downgradedBusinessId = null;
    patch.downgradedBusinessIds = null;
    patch.downgradedAt = null;
  }

  if (accountType === ACCOUNT_TYPES.INDIVIDUAL) {
    patch.primaryBusinessId = null;
  }

  const startedAt = Date.now();
  console.info("[AccountType] setUserAccountType start", {
    uid,
    accountType,
    startedAt,
  });

  try {
    // Upsert for first-time users who may not have a Firestore profile doc yet.
    await setDoc(userRef, patch, { merge: true });
    console.info("[AccountType] setUserAccountType success", {
      uid,
      accountType,
      durationMs: Date.now() - startedAt,
    });
  } catch (error) {
    console.error("[AccountType] setUserAccountType failed", {
      uid,
      accountType,
      durationMs: Date.now() - startedAt,
      errorCode: error?.code || null,
      errorMessage: error?.message || String(error),
    });
    throw error;
  }
  return patch;
};

export const setUserOnboardingStatus = async (uid, onboardingStatus) => {
  if (!uid) throw new Error("UID is undefined.");
  if (!Object.values(ONBOARDING_STATUS).includes(onboardingStatus)) {
    throw new Error("Invalid onboardingStatus value.");
  }

  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, { onboardingStatus });
  return { onboardingStatus };
};

/**
 * Fetches the current user's Firestore document.
 * @returns {Promise<import("firebase/firestore").DocumentSnapshot>} A promise that resolves with the user document snapshot.
 * @throws {Error} If there is no authenticated user or the document does not exist.
 */
export async function getUserDoc() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("No authenticated user found.");
  }

  const userDocRef = doc(db, "users", user.uid);
  const userDocSnap = await getDoc(userDocRef);

  if (!userDocSnap.exists()) {
    throw new Error("User document does not exist.");
  }

  return userDocSnap;
}
